import React, { useState, useRef, useEffect } from "react";
import {
  X,
  CheckCircle,
  RefreshCw,
  Phone,
  Receipt,
} from "lucide-react";

export enum VerificationType {
  GSTIN = "GSTIN",
  PhoneNumber = "PhoneNumber",
}

export interface VerificationModalProps {
  onClose: () => void;
  verificationType: VerificationType;
  verificationValue: string;
  onValueEdit: () => void;
  onVerificationSuccess: (otp: string) => Promise<any>;
  onResendOTP?: () => Promise<void>;
  isResendingOTP?: boolean;
  gstinUserName?: string;
  /** When true, copy reflects a resumed unfinished signup (OTP resent). */
  isResumedVerification?: boolean;
}

export default function VerificationModal({
  onClose,
  verificationType,
  verificationValue,
  onValueEdit,
  onVerificationSuccess,
  onResendOTP,
  isResendingOTP = false,
  gstinUserName,
  isResumedVerification = false,
}: VerificationModalProps) {
  const [timer, setTimer] = useState(
    verificationType === VerificationType.GSTIN ? 600 : 254
  );
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isOtpValid, setIsOtpValid] = useState<boolean | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const getVerificationDetails = () => {
    switch (verificationType) {
      case VerificationType.GSTIN:
        return {
          Icon: Receipt,
          title: "Verify your GSTIN",
          messagePrefix: "A 6-digit code has been sent to mobile number linked with",
          valuePrefix: "GSTIN : ",
          allowValueEdit: true,
          resumeNote: null as string | null,
        };
      case VerificationType.PhoneNumber:
        if (isResumedVerification) {
          return {
            Icon: Phone,
            title: "Complete your signup",
            messagePrefix:
              "We found an unfinished signup for this email. A new code has been sent to",
            valuePrefix: "",
            allowValueEdit: false,
            resumeNote:
              "After verifying, sign in with the password from your first signup attempt.",
          };
        }
        return {
          Icon: Phone,
          title: "Verify your Mobile Number",
          messagePrefix: "A 6-digit code has been sent to",
          valuePrefix: "",
          allowValueEdit: true,
          resumeNote: null as string | null,
        };
      default:
        return {
          Icon: Phone,
          title: "Verify",
          messagePrefix: "A 6-digit code has been sent to",
          valuePrefix: "",
          allowValueEdit: true,
          resumeNote: null as string | null,
        };
    }
  };

  const verificationDetails = getVerificationDetails();

  useEffect(() => {
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          setIsSessionExpired(true);
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const handleChange = (index: number, value: string) => {
    if (value.length > 1) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
    if (isVerified) {
      setIsVerified(false);
      setIsOtpValid(null);
      setShowSuccessMessage(false);
      setErrorMessage("");
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    if (isSessionExpired) return;
    if (otp.some((digit) => digit === "")) {
      setErrorMessage("Please enter all 6 digits of the OTP");
      return;
    }
    setIsVerifying(true);
    setErrorMessage("");
    try {
      await onVerificationSuccess(otp.join(""));
      setIsVerified(true);
      setIsOtpValid(true);
      setShowSuccessMessage(true);
    } catch (error: unknown) {
      console.error("OTP verification error:", error);
      setIsVerified(true);
      setIsOtpValid(false);
      setShowSuccessMessage(false);
      const message =
        error instanceof Error ? error.message : typeof error === "string" ? error : "";
      if (
        message.toLowerCase().includes("expired") ||
        (error as { response?: { data?: { type?: string } } })?.response?.data?.type ===
          "SERVICE_ERROR"
      ) {
        setErrorMessage("Your verification session has expired. Please request a new OTP.");
        setIsSessionExpired(true);
      } else if (
        message.toLowerCase().includes("incorrect") ||
        message.toLowerCase().includes("invalid")
      ) {
        setErrorMessage("The OTP you entered is incorrect. Please try again.");
      } else {
        setErrorMessage("Verification failed. Please try again or request a new OTP.");
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOTP = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (onResendOTP) {
      try {
        await onResendOTP();
        setTimer(verificationType === VerificationType.GSTIN ? 600 : 254);
        setIsSessionExpired(false);
        setOtp(["", "", "", "", "", ""]);
        setIsVerified(false);
        setIsOtpValid(null);
        setShowSuccessMessage(false);
        setErrorMessage("");
      } catch (error: unknown) {
        console.error("Resend OTP error:", error);
        setErrorMessage("Failed to resend OTP. Please try again.");
      }
    }
  };

  const shouldShowResend = () => {
    if (verificationType === VerificationType.GSTIN) return isSessionExpired;
    return true;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-[2rem] max-w-md w-full relative">
        <button onClick={onClose} className="absolute top-[1rem] right-[1rem] bg-white" aria-label="Close">
          <X />
        </button>

        <div className="flex justify-center mb-[1rem]">
          <div className="rounded-full p-3">
            <verificationDetails.Icon className="h-12 w-12 text-[#6680BB]" aria-hidden />
          </div>
        </div>

        <div className="mb-[1.5rem] text-center">
          <h2 className="text-[1.5rem] font-medium text-gray-700 mb-[0.5rem]">
            {verificationDetails.title}
          </h2>
        </div>

        {showSuccessMessage && (
          <div className="mb-4 flex flex-col items-center justify-center text-green-600">
            <CheckCircle size={48} className="mb-2" />
            <p className="text-lg font-medium">Verification Successful!</p>
            <p className="text-sm">Proceeding to the next step...</p>
          </div>
        )}

        {isSessionExpired && !showSuccessMessage && (
          <div className="mb-4 flex flex-col items-center justify-center text-amber-600">
            <RefreshCw size={32} className="mb-2" />
            <p className="text-md font-medium">Session Expired</p>
            <p className="text-sm text-center mb-2">
              Your verification session has expired. Please use the resend option below.
            </p>
          </div>
        )}

        {!showSuccessMessage && (
          <div className="text-center text-gray-600 mb-[1.5rem]">
            <p>
              {verificationDetails.messagePrefix}
              <br />
              <span className="font-semibold">
                {verificationDetails.valuePrefix}{verificationValue}
              </span>
              {verificationDetails.allowValueEdit ? (
                <>
                  {"  "}
                  <span
                    className="text-blue-600 hover:text-blue-800 underline cursor-pointer font-semibold"
                    onClick={onValueEdit}
                  >
                    change
                  </span>
                </>
              ) : null}
            </p>
            {verificationDetails.resumeNote ? (
              <p className="mt-[0.75rem] text-[0.875rem] text-gray-500">
                {verificationDetails.resumeNote}
              </p>
            ) : null}
          </div>
        )}

        {!showSuccessMessage && (
          <div className="mb-[1.5rem]">
            <p className="text-center text-gray-600 mb-[0.75rem]">Enter code here</p>
            <div className={`flex justify-center gap-[0.5rem] ${isVerified ? (isOtpValid ? "otp-input-valid" : "otp-input-invalid") : ""}`}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength={1}
                  value={digit}
                  ref={(el) => { if (el) inputRefs.current[index] = el; }}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className={`w-[3rem] h-[3rem] border rounded-md text-center text-[1.25rem] ${isVerified ? (isOtpValid ? "border-green-500 text-green-700" : "border-red-500 text-red-700") : ""}`}
                  disabled={isVerifying || (isSessionExpired && !isResendingOTP)}
                />
              ))}
            </div>
            {errorMessage && (
              <p className="text-red-500 text-sm text-center mt-2">{errorMessage}</p>
            )}
          </div>
        )}

        {!showSuccessMessage && !isSessionExpired && (
          <p className="text-center text-gray-600 mb-[0.5rem]">
            OTP will be expired in {formatTime(timer)}
          </p>
        )}

        {!showSuccessMessage && shouldShowResend() && (
          <p className="text-center text-gray-600 mb-[1.5rem]">
            {isSessionExpired ? "Session expired. " : "Didn't receive the code? "}
            <a
              href="#"
              onClick={handleResendOTP}
              className={`text-blue-600 hover:underline ${isResendingOTP ? "opacity-50 pointer-events-none" : ""}`}
            >
              {isResendingOTP ? "Sending..." : "Resend OTP"}
            </a>
          </p>
        )}

        {!showSuccessMessage && (
          <button
            onClick={handleVerify}
            disabled={isVerifying || otp.some((digit) => digit === "") || isSessionExpired}
            className={`w-full bg-blue-900 text-white py-[0.75rem] rounded-md hover:bg-blue-800 transition-colors ${isVerifying || otp.some((digit) => digit === "") || isSessionExpired ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {isVerifying ? "Verifying..." : isSessionExpired ? "Session Expired" : "Verify"}
          </button>
        )}
      </div>
    </div>
  );
}
